import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';
import { PaymentReminderType } from 'src/types/enums';

import type { PaymentRemindersResponse } from './endpoints/PaymentRemindersEndpoint.types';
import type { PaymentReminderDetail } from './endpoints/PaymentReminderEndpoint.types';
import type {
  InvoiceForReminder,
  InvoiceStatusValue,
  PaymentReminderInvoicesResponse,
} from './endpoints/PaymentReminderInvoicesEndpoint.types';
import type { CreatePaymentReminderResponse } from './endpoints/CreatePaymentReminderEndpoint.types';
import type { SendPaymentReminderResponse } from './endpoints/SendPaymentReminderEndpoint.types';
import type { GeneratePaymentReminderResponse } from './endpoints/GeneratePaymentReminderEndpoint.types';

// Cleaner re-exports for consumers.
export type PaymentReminder = PaymentReminderDetail;
export type PaymentReminderInvoice = InvoiceForReminder;

export const PAYMENT_REMINDER_SWR_KEY = 'payment-reminder';
export const PAYMENT_REMINDER_INVOICES_SWR_KEY = 'payment-reminder-invoices';

export interface PaymentRemindersFilters {
  search?: string;
  customerId?: number;
}

export async function getPaymentReminders(
  cursor?: string | null,
  take?: number,
  filters?: PaymentRemindersFilters,
  sortBy?: string,
  sortOrder?: string,
): Promise<PaymentRemindersResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set('id', cursor);
  if (take != null) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);
  if (filters?.customerId != null) params.set('customerId', filters.customerId.toString());
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);

  const queryString = params.toString();
  const url = queryString ? `/api/v1/payment-reminders?${queryString}` : '/api/v1/payment-reminders';
  return fetchEndpointData<PaymentRemindersResponse>(url);
}

export async function getPaymentReminder(id: string): Promise<PaymentReminderDetail> {
  return fetchEndpointData<PaymentReminderDetail>(`/api/v1/payment-reminders/${id}`);
}

export interface PaymentReminderInvoicesFilters {
  search?: string;
  customerId?: number;
  status?: InvoiceStatusValue[];
}

export async function getPaymentReminderInvoices(
  cursor?: string | null,
  take?: number,
  filters?: PaymentReminderInvoicesFilters,
): Promise<PaymentReminderInvoicesResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set('id', cursor);
  if (take != null) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);
  if (filters?.customerId != null) params.set('customerId', filters.customerId.toString());
  if (filters?.status) {
    for (const status of filters.status) {
      params.append('status', status);
    }
  }

  const queryString = params.toString();
  const url = queryString ? `/api/v1/payment-reminders/invoices?${queryString}` : '/api/v1/payment-reminders/invoices';
  return fetchEndpointData<PaymentReminderInvoicesResponse>(url);
}

export async function createPaymentReminder(body: {
  customerId: number;
  // The string enum name (e.g. 'FirstReminder'); the server maps it to the
  // Prisma Int. Matches the original GraphQL PaymentReminderType input contract.
  reminderType: PaymentReminderType;
  invoiceIds: string[];
}): Promise<CreatePaymentReminderResponse> {
  return fetchEndpointData<CreatePaymentReminderResponse>('/api/v1/payment-reminders', {
    method: 'POST',
    body,
  });
}

export async function sendPaymentReminder(
  paymentReminderId: string,
  contactIds: number[],
): Promise<SendPaymentReminderResponse> {
  return fetchEndpointData<SendPaymentReminderResponse>(`/api/v1/payment-reminders/${paymentReminderId}/send`, {
    method: 'POST',
    body: { contactIds },
  });
}

export async function generatePaymentReminder(paymentReminderId: string): Promise<GeneratePaymentReminderResponse> {
  return fetchEndpointData<GeneratePaymentReminderResponse>(`/api/v1/payment-reminders/${paymentReminderId}/generate`, {
    method: 'POST',
  });
}

export function usePaymentReminder(id: string) {
  return useSWR([PAYMENT_REMINDER_SWR_KEY, id], () => getPaymentReminder(id));
}

export function usePaymentReminderInvoices(take: number, filters: PaymentReminderInvoicesFilters) {
  return useSWR([PAYMENT_REMINDER_INVOICES_SWR_KEY, take, filters.search, filters.customerId, filters.status], () =>
    getPaymentReminderInvoices(undefined, take, filters),
  );
}
